import type { KyInstance } from 'ky';

import { ensureError, formatString, isNotDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { PullRecencyCommand } from './types/pull-recency-command.type';

/**
 * Pulls recency data for a mailing.
 *
 * @param instance - The Ky instance to use for the request.
 * @param data - The command data containing client acronym, mailing ID, and mailing name.
 * @returns A promise that resolves to the operation result of the recency pull.
 * @throws An error if the client acronym, mailing name, or mailing ID is missing, or if the request fails.
 */
export const pullRecency = async (instance: KyInstance, data: PullRecencyCommand) => {
  try {
    const { clientAcronym, mailingId, mailingName } = data;

    if (isNotDefined(clientAcronym) || isNotDefined(mailingName) || isNotDefined(mailingId)) {
      throw new Error('Client acronym, mailing name, and mailing ID are required for recency pull.');
    }

    return await instance
      .post<OperationResultWithData<number>>(formatString(Uri.mailing.recency, mailingId.toString()), {
        json: data,
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
